import * as React from 'react'
import { useDispatch } from 'react-redux'
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material'
import { DeleteOutline, Edit } from '@mui/icons-material'
import { red } from '@mui/material/colors'
import { setIsStored } from '../../../redux/slices/Alerts'
import * as RT from '../../../config/routeType'
import { useCities } from '../../../context/CitiesContext'
import { resolveTreeRoute } from '../../../services'
import { buildCantonUrl } from '../../../utils/cantonUrl'

const ACTION_BUTTON_SX = {
  width: 36,
  height: 36,
}

const TENANT_STORAGE_KEY = 'ArbolEc_selectedTenant'

const escapeHtml = value =>
  String(value || '').replace(/[&<>"']/g, char => {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    }
    return entities[char]
  })

const getTenantId = tenant => tenant?.id ?? tenant?.id_t ?? tenant?.tenantId

const resolveCantonAndTenantFromTree = (tree, cantones) => {
  const treeTenantId = tree?.tenantId == null ? null : String(tree.tenantId)
  if (!treeTenantId) return { canton: null, tenant: null }

  for (const canton of cantones || []) {
    const tenant = (canton.tenants || []).find(t => String(getTenantId(t)) === treeTenantId)
    if (tenant) return { canton, tenant }
  }

  return { canton: null, tenant: null }
}

const buildAdminEditPath = (tree, cantones) => {
  if (!tree?.uuid) return null

  const { canton } = resolveCantonAndTenantFromTree(tree, cantones)
  if (!canton?.slug) return null

  return buildCantonUrl(
    canton.slug,
    cantones,
    `/${RT.MANAGEMENT_TREE}/${encodeURIComponent(tree.uuid)}`,
  )
}

const getUniqueIdentifiers = tree =>
  [tree?.uuid, tree?.codigo]
    .map(value => String(value || '').trim())
    .filter(Boolean)
    .filter((value, index, list) => list.indexOf(value) === index)

const setTenantForEditWindow = (editWindow, tenantSlug) => {
  if (!tenantSlug || !editWindow || editWindow.closed) return

  try {
    editWindow.sessionStorage.setItem(TENANT_STORAGE_KEY, tenantSlug)
  } catch {
    // Si el navegador aísla la pestaña, la edición igual carga por URL territorial.
  }
}

const writeEditWindowState = (editWindow, title, message) => {
  if (!editWindow || editWindow.closed) return

  try {
    editWindow.document.open()
    editWindow.document.write(`
      <!doctype html>
      <html lang="es">
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(title)}</title>
          <style>
            body {
              margin: 0;
              min-height: 100vh;
              display: grid;
              place-items: center;
              font-family: Arial, sans-serif;
              color: #1f2937;
              background: #f8fafc;
            }
            main {
              width: min(420px, calc(100vw - 48px));
              padding: 28px;
              border: 1px solid #dbe3ef;
              border-radius: 12px;
              background: #ffffff;
              box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
            }
            h1 {
              margin: 0 0 10px;
              font-size: 20px;
              line-height: 1.25;
            }
            p {
              margin: 0;
              color: #64748b;
              font-size: 15px;
              line-height: 1.5;
            }
          </style>
        </head>
        <body>
          <main>
            <h1>${escapeHtml(title)}</h1>
            <p>${escapeHtml(message)}</p>
          </main>
        </body>
      </html>
    `)
    editWindow.document.close()
  } catch {
    // La ventana puede haber sido cerrada por el usuario antes de recibir la ruta.
  }
}

const TreeActions = ({ tree, onRequestDelete }) => {
  const dispatch = useDispatch()
  const { getEnabledCantones } = useCities()
  const [editLoading, setEditLoading] = React.useState(false)

  const cantones = getEnabledCantones()
  const { tenant } = React.useMemo(
    () => resolveCantonAndTenantFromTree(tree, cantones),
    [tree, cantones],
  )
  const adminEditPath = React.useMemo(
    () => buildAdminEditPath(tree, cantones),
    [tree, cantones],
  )

  const stopGridInteraction = event => {
    event?.stopPropagation?.()
  }

  const handleDeleteClick = event => {
    stopGridInteraction(event)
    onRequestDelete(tree)
  }

  const handleEditClick = async event => {
    stopGridInteraction(event)
    if (editLoading) return

    const editWindow = window.open('', '_blank')
    if (editWindow) {
      editWindow.opener = null
      writeEditWindowState(
        editWindow,
        'Preparando edición',
        'Validando la ruta correcta del árbol seleccionado.',
      )
      editWindow.focus()
    }

    setEditLoading(true)

    try {
      const identifiers = getUniqueIdentifiers(tree)
      let result = null
      let editPath = null

      for (const identifier of identifiers) {
        result = await resolveTreeRoute(identifier)
        editPath = result?.paths?.edit
        if (result?.found && editPath) break
      }

      const targetEditPath = editPath || adminEditPath

      if (!targetEditPath) {
        writeEditWindowState(
          editWindow,
          'No se pudo abrir la edición',
          result?.message ||
            'No se pudo resolver el cantón y tenant correcto del árbol.',
        )
        dispatch(
          setIsStored({
            stored: false,
            message:
              result?.message ||
              'No se pudo resolver el cantón y tenant correcto del árbol seleccionado.',
          }),
        )
        return
      }

      setTenantForEditWindow(editWindow, tenant?.slug)

      if (editWindow && !editWindow.closed) {
        editWindow.location.replace(targetEditPath)
      } else {
        if (tenant?.slug) {
          try {
            window.sessionStorage.setItem(TENANT_STORAGE_KEY, tenant.slug)
          } catch { /* ignore */ }
        }
        window.location.assign(targetEditPath)
      }
    } catch {
      writeEditWindowState(
        editWindow,
        'No se pudo abrir la edición',
        'Ocurrió un error al validar la ruta correcta del árbol.',
      )
      dispatch(
        setIsStored({
          stored: false,
          message: 'No se pudo validar la ruta correcta del árbol seleccionado.',
        }),
      )
    } finally {
      setEditLoading(false)
    }
  }

  return (
    <Box
      sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}
      onClick={stopGridInteraction}
      onMouseDown={stopGridInteraction}>
      <Tooltip title="Eliminar árbol">
        <span>
          <IconButton
            size="small"
            onClick={handleDeleteClick}
            aria-label="Eliminar árbol"
            sx={{
              ...ACTION_BUTTON_SX,
              color: red[600],
              '&:hover': { bgcolor: red[50] },
            }}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Editar árbol">
        <IconButton
          size="small"
          sx={ACTION_BUTTON_SX}
          onClick={handleEditClick}
          disabled={editLoading}
          aria-label="Editar árbol">
          {editLoading ? <CircularProgress size={16} /> : <Edit fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export default TreeActions
